import React from 'react';
import styles from './CourseList.module.css';
import * as utils from '../../../utils/utils';
import Sidebar from '../../Sidebar/Sidebar';

const CATEGORY_META = {
	skill: {
		title: 'Skill Gap Courses',
		description: 'Targeted pathways to close your role-critical skill gaps.',
		key: 'skills',
	},
	tool: {
		title: 'Tool Gap Courses',
		description: 'Hands-on learning to master tools used in your target jobs.',
		key: 'tools',
	},
	domain: {
		title: 'Domain Gap Courses',
		description: 'Deepen domain understanding expected by hiring teams.',
		key: 'domains',
	},
	'soft-skill': {
		title: 'Soft Skill Courses',
		description: 'Build communication, ownership, and collaboration strengths.',
		key: 'softSkills',
	},
	certificate: {
		title: 'Certificate Prep Courses',
		description: 'Structured tracks for industry-recognized certifications.',
		key: 'certificates',
	},
};

export default class CourseList extends React.Component {
	constructor(props) {
		super(props);

		this.categoryOrder = ['skill', 'tool', 'domain', 'soft-skill', 'certificate'];

		this.state = {
			isLoading: true,
			errorText: '',
			courseBuckets: {
				skills: [],
				tools: [],
				domains: [],
				softSkills: [],
				certificates: [],
			},
		};
	}

	componentDidMount() {
		if (!utils.isLoggedIn()) {
			window.location.href = '/signin';
			return;
		}

		this.loadCourseList();
	}

	loadCourseList = () => {
		this.setState({
			isLoading: true,
			errorText: '',
		}, () => {
			utils.sendRequest('/course/getCourseList', {})
				.then((responseData) => {
					this.setState({
						isLoading: false,
						courseBuckets: {
							skills: responseData.skills || [],
							tools: responseData.tools || [],
							domains: responseData.domains || [],
							softSkills: responseData.softSkills || [],
							certificates: responseData.certificates || [],
						},
					});
				})
				.catch((error) => {
					this.setState({
						isLoading: false,
						errorText: error && error.message ? error.message : 'Could not load your courses right now.',
					});
				});
		});
	};

	goToCourseItemList = (itemType) => {
		window.location.href = `/course-item-list?itemType=${encodeURIComponent(itemType)}`;
	};

	goToCourseDetail = (courseId) => {
		window.location.href = `/course-detail?courseID=${courseId}&chapterNum=1`;
	};

	renderCoursePreview = (course, index) => {
		return (
			<button
				key={`course-preview-${course.courseId}-${index}`}
				type='button'
				className={styles.courseCard}
				onClick={() => this.goToCourseDetail(course.courseId)}
			>
				<div className={styles.courseTopRow}>
					<span className={styles.itemPill}>{course.itemName || 'Course'}</span>
				</div>
				<h4 className={styles.courseTitle}>{course.courseTitle || 'Untitled Course'}</h4>
				<p className={styles.courseMeta}>Recommended for {course.jobTitle || 'your target role'}</p>
			</button>
		);
	};

	renderCategoryPanel = (itemType) => {
		const meta = CATEGORY_META[itemType];
		const courses = this.state.courseBuckets[meta.key] || [];

		return (
			<section key={`category-${itemType}`} className={styles.categoryPanel}>
				<div className={styles.categoryHeader}>
					<div>
						<h3 className={styles.categoryTitle}>{meta.title}</h3>
						<p className={styles.categoryText}>{meta.description}</p>
					</div>
					{
						courses.length > 0 &&
						<button
							type='button'
							className={styles.showAllButton}
							onClick={() => this.goToCourseItemList(itemType)}
						>
							Show All
						</button>
					}
				</div>

				{
					courses.length === 0
						? <p className={styles.emptyText}>No courses yet in this category. New recommendations will appear here.</p>
						: <div className={styles.courseGrid}>{courses.map(this.renderCoursePreview)}</div>
				}
			</section>
		);
	};

	render() {
		const {
			isLoading,
			errorText,
		} = this.state;

		return (
			<div className={styles.pageContainer}>
				<Sidebar />

				<div className={styles.glowTop} />
				<div className={styles.glowBottom} />

				<div className={styles.layoutShell}>
					<section className={styles.headerPanel}>
						<div className={styles.brandBadge}>HQVibe</div>
						<h1 className={styles.title}>Your Professional Learning Roadmap</h1>
						<p className={styles.subtitle}>
							Explore curated courses mapped to your role gaps across skills, tools, domains, soft skills, and certifications.
						</p>
					</section>

					<section className={styles.mainCardPanel}>
						<div className={styles.stepRow}>
							<div className={`${styles.stepDot} ${styles.stepDotActive}`} />
							<div className={styles.stepLine} />
							<div className={`${styles.stepDot} ${styles.stepDotActive}`} />
						</div>

						<h2 className={styles.formTitle}>Course Categories</h2>
						<p className={styles.formText}>Click any course to open details, or use Show All to browse each full category.</p>

						{
							isLoading
								? <div className={styles.loadingBox}>Loading your courses...</div>
								: <div className={styles.categoryStack}>{this.categoryOrder.map(this.renderCategoryPanel)}</div>
						}

						{
							errorText !== '' &&
							<div className={styles.errorBox}>{errorText}</div>
						}
					</section>
				</div>
			</div>
		);
	}
}
